package fr.manyverse.app.presentation.components.modules.announcement

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.LinearProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.input.KeyboardCapitalization
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.DialogProperties
import fr.manyverse.app.domain.model.ImageData
import fr.manyverse.app.domain.model.TableMember
import fr.manyverse.app.presentation.components.ImagePickerPreview
import fr.manyverse.app.presentation.components.clearFocusOnTap
import fr.manyverse.app.presentation.components.modules.common.ParticipantSelectionMode
import fr.manyverse.app.presentation.components.modules.common.ParticipantSelector
import manyverse.shared.generated.resources.Res
import manyverse.shared.generated.resources.announcement_image
import manyverse.shared.generated.resources.announcement_text
import manyverse.shared.generated.resources.announcement_text_hint
import manyverse.shared.generated.resources.announcement_title
import manyverse.shared.generated.resources.announcement_validation_error
import manyverse.shared.generated.resources.cancel
import manyverse.shared.generated.resources.send
import org.jetbrains.compose.resources.stringResource

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun AnnouncementCreationDialog(
    showDialog: Boolean,
    players: List<TableMember>,
    labelOf: (TableMember) -> String = { it.userId.take(8) },
    selectedText: String,
    selectedImageData: ImageData?,
    selectedParticipantIds: Set<String>,
    allSelected: Boolean,
    isLoading: Boolean,
    errorMessage: String?,
    onTextChange: (String) -> Unit,
    onPickImage: () -> Unit,
    onClearImage: () -> Unit,
    onToggleAllSelected: () -> Unit,
    onToggleParticipant: (String) -> Unit,
    onDismiss: () -> Unit,
    onConfirm: () -> Unit,
    modifier: Modifier = Modifier
) {
    val canSubmit = (selectedText.isNotBlank() || selectedImageData != null) &&
            (allSelected || selectedParticipantIds.isNotEmpty()) &&
            !isLoading

    if (!showDialog) return

    AlertDialog(
        properties = DialogProperties(dismissOnBackPress = false, dismissOnClickOutside = false),
        onDismissRequest = {
            if (!isLoading) {
                onDismiss()
            }
        },
        title = {
            Text(stringResource(Res.string.announcement_title))
        },
        text = {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .verticalScroll(rememberScrollState()),
                verticalArrangement = Arrangement.spacedBy(16.dp)
            ) {
                // Participants selection
                ParticipantSelector(
                    players = players,
                    mode = ParticipantSelectionMode.Multiple(
                        selectedIds = selectedParticipantIds,
                        allSelected = allSelected,
                        onToggle = onToggleParticipant,
                        onToggleAll = onToggleAllSelected
                    ),
                    labelOf = labelOf,
                    isEnabled = !isLoading
                )

                // Text input
                OutlinedTextField(
                    value = selectedText,
                    onValueChange = { if (it.length <= 500) onTextChange(it) },
                    label = { Text(stringResource(Res.string.announcement_text)) },
                    placeholder = { Text(stringResource(Res.string.announcement_text_hint)) },
                    modifier = Modifier
                        .fillMaxWidth()
                        .height(120.dp),
                    maxLines = 5,
                    enabled = !isLoading,
                    supportingText = {
                        Text("${selectedText.length}/500")
                    },
                    keyboardOptions = KeyboardOptions(capitalization = KeyboardCapitalization.Sentences)
                )

                // Image picker
                Column {
                    Text(
                        text = stringResource(Res.string.announcement_image),
                        style = MaterialTheme.typography.labelMedium
                    )
                    ImagePickerPreview(
                        selectedImageData = selectedImageData,
                        onPickImage = onPickImage,
                        onClearImage = onClearImage,
                        pickLabel = stringResource(Res.string.announcement_image),
                        enabled = !isLoading,
                        modifier = Modifier.padding(top = 8.dp)
                    )
                }

                // Validation error
                if (errorMessage != null) {
                    Text(
                        text = errorMessage,
                        color = MaterialTheme.colorScheme.error,
                        style = MaterialTheme.typography.bodySmall
                    )
                }

                // Validation hint
                if (selectedText.isBlank() && selectedImageData == null) {
                    Text(
                        text = stringResource(Res.string.announcement_validation_error),
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                        style = MaterialTheme.typography.bodySmall
                    )
                }

                // Loading indicator
                if (isLoading) {
                    LinearProgressIndicator(
                        modifier = Modifier.fillMaxWidth()
                    )
                }
            }
        },
        confirmButton = {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                TextButton(
                    onClick = onDismiss,
                    enabled = !isLoading
                ) {
                    Text(stringResource(Res.string.cancel))
                }
                Button(
                    onClick = onConfirm,
                    enabled = canSubmit
                ) {
                    Text(stringResource(Res.string.send))
                }
            }
        },
        modifier = modifier.clearFocusOnTap()
    )
}
